/**
 * API 호출용 유효성 검증 유틸리티
 */

/**
 * 날짜를 YYYYMMDD 형식으로 정규화
 */
export const normalizeDate = (date: string): string => {
  // 하이픈과 슬래시를 제외한 비숫자 문자 제거
  const cleanDate = date.trim();

  // 이미 YYYYMMDD 형식인 경우
  if (/^\d{8}$/.test(cleanDate)) {
    return cleanDate;
  }

  // YYYY-MM-DD 형식인 경우
  if (/^\d{4}-\d{2}-\d{2}$/.test(cleanDate)) {
    return cleanDate.replace(/-/g, '');
  }

  // YYYY/MM/DD 형식인 경우
  if (/^\d{4}\/\d{2}\/\d{2}$/.test(cleanDate)) {
    return cleanDate.replace(/\//g, '');
  }

  throw new Error(`잘못된 날짜 형식: ${date}. YYYYMMDD 또는 YYYY-MM-DD 형식이어야 합니다`);
};

/**
 * 스포츠 종목 파라미터 검증 (쉼표로 구분된 다중 종목 지원)
 */
export const validateSport = (sport: string): string => {
  const validSports = ['soccer', 'baseball', 'basketball', 'volleyball', 'hockey'];

  // 쉼표로 구분된 다중 종목 지원
  const sports = sport.split(',').map(s => s.trim().toLowerCase());

  for (const s of sports) {
    if (!validSports.includes(s)) {
      throw new Error(`잘못된 종목: ${s}. 사용 가능한 종목: ${validSports.join(', ')}`);
    }
  }

  // 검증된 종목들을 쉼표로 연결하여 반환
  return sports.join(',');
};

/**
 * AI 프로바이더 검증
 */
export const validateAIProvider = (provider: string): string => {
  const validProviders = ['claude', 'openai', 'gemini'];
  const providerLower = provider.toLowerCase();

  if (!validProviders.includes(providerLower)) {
    throw new Error(`잘못된 AI 프로바이더: ${provider}. 사용 가능한 프로바이더: ${validProviders.join(', ')}`);
  }

  return providerLower;
};

/**
 * API 파라미터 문자열 정제
 */
export const sanitizeParam = (param: string | undefined): string | undefined => {
  if (!param) return undefined;

  // 문제를 일으킬 수 있는 문자 제거
  return param.trim()
    .replace(/[<>]/g, '')  // 잠재적 HTML 태그 제거
    .replace(/[\n\r]/g, ' ');  // 개행 문자를 공백으로 대체
};

/**
 * 경기 시간 형식 검증
 */
export const validateMatchTime = (time: string | number | undefined): string | undefined => {
  if (!time) return undefined;

  // 숫자인 경우 (자정 기준 초)
  if (typeof time === 'number' || /^\d+(\.\d+)?$/.test(String(time))) {
    const seconds = typeof time === 'number' ? time : parseFloat(String(time));
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  // 이미 HH:MM 형식인 경우
  if (typeof time === 'string' && /^\d{1,2}:\d{2}$/.test(time)) {
    const [hours, minutes] = time.split(':').map(Number);
    if (hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }
  }

  throw new Error(`잘못된 시간 형식: ${time}. HH:MM 또는 초 단위여야 합니다`);
};
